import type { CapturedAudio } from '../audio/AudioInput';
import type { AssistantResponse, DisplayType, LocationContext } from '../types';
import { DEFAULT_REQUEST_TIMEOUT_MS } from './constants';

const DISPLAY_TYPES: readonly DisplayType[] = ['text', 'map', 'weather', 'mixed'];

export interface ConversationClientOptions {
  apiBaseUrl: string;
  timeoutMs?: number;
}

/**
 * A safe, user-facing error only — never wraps provider secrets or raw
 * backend stack traces. `code` lets the SDK/publisher branch on failure
 * kind without parsing message text.
 */
export class ConversationApiError extends Error {
  constructor(
    message: string,
    public readonly code: string,
  ) {
    super(message);
    this.name = 'ConversationApiError';
  }
}

function isValidAssistantResponse(value: unknown): value is AssistantResponse {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<AssistantResponse>;
  return (
    typeof candidate.transcript === 'string' &&
    typeof candidate.message === 'object' &&
    candidate.message !== null &&
    typeof candidate.message.text === 'string' &&
    typeof candidate.message.language === 'string' &&
    typeof candidate.display === 'object' &&
    candidate.display !== null &&
    DISPLAY_TYPES.includes(candidate.display.type as DisplayType) &&
    typeof candidate.data === 'object' &&
    candidate.data !== null &&
    typeof candidate.sessionLimitReached === 'boolean'
  );
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
}

/**
 * Talks only to OUR backend's conversation API — this module has no
 * knowledge of Gemini and never sees a provider credential. The backend is
 * the sole holder of the Gemini API key.
 */
export class ConversationClient {
  constructor(private readonly options: ConversationClientOptions) {}

  async sendAudio(
    sessionId: string,
    audio: CapturedAudio,
    location?: LocationContext,
    signal?: AbortSignal,
  ): Promise<AssistantResponse> {
    const base64 = await blobToBase64(audio.blob);
    return this.post(
      { sessionId, audio: { data: base64, mimeType: audio.mimeType }, location },
      signal,
    );
  }

  private async post(
    body: {
      sessionId: string;
      audio: { data: string; mimeType: string };
      location?: LocationContext;
    },
    externalSignal?: AbortSignal,
  ): Promise<AssistantResponse> {
    const timeoutMs = this.options.timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    if (externalSignal) {
      if (externalSignal.aborted) controller.abort();
      else externalSignal.addEventListener('abort', () => controller.abort(), { once: true });
    }

    try {
      let res: Response;
      try {
        res = await fetch(`${this.options.apiBaseUrl}/conversation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          signal: controller.signal,
        });
      } catch (error) {
        if ((error as { name?: string }).name === 'AbortError') {
          throw new ConversationApiError('The assistant took too long to respond.', 'TIMEOUT');
        }
        throw new ConversationApiError('Could not reach the assistant backend.', 'NETWORK_ERROR');
      }

      const payload: unknown = await res.json().catch(() => null);

      if (!res.ok) {
        const errorPayload = payload as { error?: { code?: string; message?: string } } | null;
        throw new ConversationApiError(
          errorPayload?.error?.message ?? 'The assistant could not process that request.',
          errorPayload?.error?.code ?? 'BACKEND_ERROR',
        );
      }

      if (!isValidAssistantResponse(payload)) {
        throw new ConversationApiError(
          'The assistant returned an unexpected response.',
          'INVALID_RESPONSE',
        );
      }

      return payload;
    } finally {
      clearTimeout(timer);
    }
  }
}
